import React, { useState, useRef, useEffect } from 'react';
import languageLearningApi from '../../middleware/languageLearningApi';
import { Paper, Typography, Box, TextField, Button } from "@mui/material";
import VocabularyEditor from '../components/VocabularyEditor';
import GrammarConceptsEditor from '../components/GrammarConceptsEditor';

const SentenceForm = ({ courseletId, sentence, onClose }) => {
  // Basic sentence fields
  const [text, setText] = useState(sentence ? sentence.text : '');
  const [meaning, setMeaning] = useState(sentence ? sentence.meaning : '');
  const [translation, setTranslation] = useState(sentence ? sentence.translation : '');
  
  // For vocabulary, we use an array.
  const [vocabulary, setVocabulary] = useState(sentence ? sentence.vocabulary : []);
  
  // For grammar concepts, we manage both a raw input and an array.
  const [rawGrammar, setRawGrammar] = useState(
    sentence ? sentence.grammarConcepts.map(g => g.concept).join(', ') : ''
  );
  const [grammarConcepts, setGrammarConcepts] = useState(sentence ? sentence.grammarConcepts : []);

  // For field-level errors
  const [errors, setErrors] = useState({});

  // Store initial data to compare changes in edit mode
  const initialDataRef = useRef(null);
  useEffect(() => {
    if (sentence) {
      initialDataRef.current = {
        text: sentence.text,
        meaning: sentence.meaning,
        translation: sentence.translation,
        vocabulary: sentence.vocabulary,
        grammarConcepts: sentence.grammarConcepts
      };
      console.log("Initial data set for edit mode:", initialDataRef.current);
    }
  }, [sentence]);

  // Auto-fill vocabulary based on sentence text (split by space)
  const handleAutoFillVocabulary = () => {
    const words = text.split(' ').filter(w => w.trim() !== '');
    const vocabArray = words.map(word => ({
      word: word.trim(),
      partOfSpeech: '',
      meanings: []
    }));
    setVocabulary(vocabArray);
    console.log("Auto-filled vocabulary:", vocabArray);
  };

  // Parse raw grammar input (comma separated) into grammarConcept objects
  const handleParseGrammar = () => {
    const concepts = rawGrammar
      .split(',')
      .filter(g => g.trim() !== '')
      .map(concept => ({
        concept: concept.trim(),
        // Prepopulate Grammar info with two key/value pairs
        details: [
          { key: 'Part of Speech', value: '' },
          { key: 'Word Type', value: '' }
        ],
        description: '',
        examples: []
      }));
    setGrammarConcepts(concepts);
    console.log("Parsed grammar concepts:", concepts);
  };

  const validateFields = () => {
    let hasError = false;
    let newErrors = {};

    // Validate sentence fields
    if (!text.trim()) {
      newErrors.text = 'This field is required';
      hasError = true;
    }
    if (!meaning.trim()) {
      newErrors.meaning = 'This field is required';
      hasError = true;
    }
    if (!translation.trim()) {
      newErrors.translation = 'This field is required';
      hasError = true;
    }

    // Validate vocabulary: each entry must have word, partOfSpeech, and meanings.
    vocabulary.forEach((vocab, index) => {
      if (!vocab.word.trim() || !vocab.partOfSpeech || !vocab.meanings.length) {
        newErrors[`vocab_${index}`] = 'All fields (word, POS and meanings) are required';
        hasError = true;
      }
    });

    // Validate grammar concepts: every field must be filled.
    grammarConcepts.forEach((gc, index) => {
      if (!gc.concept.trim()) {
        newErrors[`grammar_${index}_concept`] = 'Concept cannot be blank';
        hasError = true;
      }
      // Validate each grammar info detail (key and value)
      if (!gc.details || !gc.details.length) {
        newErrors[`grammar_${index}_details`] = 'Grammar info required';
        hasError = true;
      } else {
        gc.details.forEach((detail, dIndex) => {
          if (!detail.key.trim() || !detail.value.trim()) {
            newErrors[`grammar_${index}_detail_${dIndex}`] = 'Both key and value are required';
            hasError = true;
          }
        });
      }
      if (!gc.description.trim()) {
        newErrors[`grammar_${index}_description`] = 'Description cannot be blank';
        hasError = true;
      }
      if (!gc.examples || !gc.examples.length) {
        newErrors[`grammar_${index}_examples`] = 'At least one example is required';
        hasError = true;
      }
    });

    setErrors(newErrors);
    if (hasError) {
      console.log("Validation errors:", newErrors);
    }
    return !hasError;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("Submit clicked");

    if (!validateFields()) {
      console.log("Validation failed. Not submitting.");
      return;
    }

    const sentenceData = { 
      text, 
      meaning, 
      translation, 
      vocabulary, 
      grammarConcepts 
    };

    console.log("Submitting sentence data:", sentenceData);
    console.log("Courselet ID:", courseletId);
    console.log("Edit mode:", sentence ? true : false);

    // If in edit mode and no changes were made, simply close without calling API.
    if (sentence && initialDataRef.current && 
        JSON.stringify(sentenceData) === JSON.stringify(initialDataRef.current)
    ) {
      console.log("No changes detected, closing form without API call.");
      onClose();
      return;
    }

    try {
      let response;
      if (sentence && sentence._id) {
        // Edit mode: update sentence using the update route
        console.log("Calling PUT to update sentence:", sentence._id);
        response = await languageLearningApi.put(`/sentences/${sentence._id}`, sentenceData);
      } else {
        // Add mode: create new sentence
        console.log("Calling POST to create a new sentence for courselet:", courseletId);
        response = await languageLearningApi.post(`/sentences/${courseletId}/sentences`, sentenceData);
      }
      console.log("API response:", response);
      const data = response.data;
      if (data.success) {
        console.log("Submission successful, closing form.");
        onClose();
      } else {
        console.log("Submission unsuccessful, response data:", data);
      }
    } catch (err) {
      console.error("Error saving sentence:", err);
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 3, mb: 2, borderRadius: 2 }}>
      <Typography variant="h6" color="primary" gutterBottom>
        {sentence ? 'Edit Sentence' : 'Add Sentence'}
      </Typography>
      <form onSubmit={handleSubmit}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2">Text:</Typography>
          <TextField 
            fullWidth 
            variant="outlined" 
            value={text} 
            onChange={(e) => setText(e.target.value)} 
            required 
            error={Boolean(errors.text)}
            helperText={errors.text}
          />
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2">Meaning:</Typography>
          <TextField 
            fullWidth 
            variant="outlined" 
            value={meaning} 
            onChange={(e) => setMeaning(e.target.value)} 
            required 
            error={Boolean(errors.meaning)}
            helperText={errors.meaning}
          />
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2">Translation:</Typography>
          <TextField 
            fullWidth 
            variant="outlined" 
            value={translation} 
            onChange={(e) => setTranslation(e.target.value)} 
            required 
            error={Boolean(errors.translation)}
            helperText={errors.translation}
          />
        </Box>
        {/* Vocabulary Section */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2">Vocabulary:</Typography>
          <Button variant="outlined" size="small" onClick={handleAutoFillVocabulary} sx={{ mb: 1 }}>
            Auto-Fill Vocabulary
          </Button>
          <VocabularyEditor 
            vocabulary={vocabulary} 
            onChange={setVocabulary} 
            errors={errors} 
          />
        </Box>
        {/* Grammar Concepts Section */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2">Grammar Concepts:</Typography>
          <TextField 
            fullWidth 
            variant="outlined" 
            placeholder="Enter comma separated grammar concepts"
            value={rawGrammar}
            onChange={(e) => setRawGrammar(e.target.value)}
            sx={{ mb: 1 }}
          />
          <Button variant="outlined" size="small" onClick={handleParseGrammar} sx={{ mb: 1 }}>
            Parse Grammar
          </Button>
          <GrammarConceptsEditor 
            grammarConcepts={grammarConcepts} 
            onChange={setGrammarConcepts}
            errors={errors}
          />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button variant="outlined" onClick={onClose}>Cancel</Button>
          <Button variant="contained" color="primary" type="submit">Submit</Button>
        </Box>
      </form>
    </Paper>
  );
};

export default SentenceForm;
